import { nonEmpty } from './dashboardFormat';
import type { SessionAnalytics, SessionDataAvailabilityItem } from '@/types/session';

export type RailPanelState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; analytics: SessionAnalytics };

export interface RailRequestState {
  analytics: SessionAnalytics | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Resolves what one rail panel shows: the request-level loading/error state first,
 * then the panel's own data availability reported by the session endpoint.
 */
export function getRailPanelState(
  { analytics, isLoading, error }: RailRequestState,
  selectAvailability?: (analytics: SessionAnalytics) => SessionDataAvailabilityItem,
  fallbackMessage = 'Session data unavailable',
): RailPanelState {
  if (analytics == null) {
    // Before the first request starts there is neither data nor an error yet: still loading.
    return isLoading || error == null
      ? { kind: 'loading' }
      : { kind: 'error', message: nonEmpty(error) ?? 'Session analytics unavailable' };
  }

  const availability = selectAvailability?.(analytics);
  if (availability?.status === 'unavailable') {
    return { kind: 'error', message: nonEmpty(availability.reason) ?? fallbackMessage };
  }

  return { kind: 'ready', analytics };
}
